import { useState, useMemo } from 'react';
import { useCsvEditorContext } from '../contexts/CsvEditorContext';
import { useRowState } from './useRowState';

/**
 * Hook for managing row filtering UI state.
 * 
 * Responsibility: Manage filter UI state (showOnlyChanged) and compute filteredRows.
 * Uses context for data and useRowState for row state computations.
 */
export const useRowFiltering = () => {
  const [showOnlyChanged, setShowOnlyChanged] = useState(false);
  const { rows, originalRowsMap, changedRowsCount } = useCsvEditorContext();
  const { changedRows } = useRowState();

  // Create a Set of changed row IDs for O(1) lookup performance
  const changedRowIdsSet = useMemo(() => {
    return new Set(changedRows.map((row) => row.id));
  }, [changedRows]);

  // Compute filtered rows based on active filters
  const filteredRows = useMemo(() => {
    // If no filters are active, return all rows
    if (!showOnlyChanged) {
      return rows;
    }

    // Filter to show only changed rows using O(1) Set lookup
    return rows.filter((row) => changedRowIdsSet.has(row.id));
  }, [rows, showOnlyChanged, changedRowIdsSet]);

  return {
    showOnlyChanged,
    setShowOnlyChanged,
    filteredRows,
    changedRowsCount,
    originalRowsMap
  };
};
